import { useEffect, useRef, useState } from 'react';
import { useLoaderData, useLocation, useNavigation, useRevalidator, useSearchParams } from 'react-router';
import { PAGE_SIZES, readListParams } from '../data/list.js';
import { ErrorAlert } from './ui.jsx';

/**
 * A searchable, paged list. The state is in the URL (?q=&sort=&dir=&page=&size=, and
 * ?reports=&open= for the checkboxes of `checkboxes`),
 * so a link or the back button opens the same list again.
 *
 * The rows come from the loader of the page (loadList in data/list.js), which runs on
 * the server: the HTML holds them for Google. A change of the URL runs the loader again.
 *
 * Keys: "/" goes to the search box; left and right arrows change the page.
 */

// A column with hideNarrow gets the class hide-narrow: app.css hides it on a narrow screen.
const cellClass = (col, base) => [base, col.hideNarrow && 'hide-narrow'].filter(Boolean).join(' ') || undefined;

export default function ListView({ title, subtitle, columns, defaultSort, placeholder, hint, checkboxes }) {
  const [params, setParams] = useSearchParams();
  const { q, page, size, sortField, desc, filters } = readListParams(params, { defaultSort, checkboxes: Boolean(checkboxes) });
  const data = useLoaderData();
  const { pathname } = useLocation();
  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const loading = revalidator.state === 'loading'
    || (navigation.state === 'loading' && navigation.location?.pathname === pathname);
  const error = data.error ? { message: data.error } : null;

  const [text, setText] = useState(q);
  const inputRef = useRef(null);

  function update(changes) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      }
      return next;
    }, { replace: true });
  }

  // The last ?q= that the box sent. The URL changes only when the loader is done, and
  // the user types more keys in that time. The box must not go back to that old ?q=.
  const sentRef = useRef(q);

  // The box follows a URL that it did not send, for example after the back button.
  useEffect(() => {
    if (q === sentRef.current) return;
    sentRef.current = q;
    setText(q);
  }, [q]);

  // A search starts 300 ms after the last key.
  useEffect(() => {
    const value = text.trim() === '' ? '' : text;
    if (value === sentRef.current) return undefined;
    const timer = setTimeout(() => {
      sentRef.current = value;
      update({ q: value || null, page: null });
    }, 300);
    return () => clearTimeout(timer);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onKey(event) {
      const typing = ['INPUT', 'SELECT', 'TEXTAREA'].includes(document.activeElement?.tagName);
      if (event.key === '/' && !typing) {
        event.preventDefault();
        inputRef.current?.focus();
        return;
      }
      if (typing || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowRight' && data.hasNext) update({ page: page + 1 });
      if (event.key === 'ArrowLeft' && page > 1) update({ page: page > 2 ? page - 1 : null });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [page, data.hasNext]); // eslint-disable-line react-hooks/exhaustive-deps

  function sortBy(field) {
    if (field === sortField) update({ sort: field, dir: desc ? 'asc' : 'desc', page: null });
    else update({ sort: field, dir: 'desc', page: null });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>

      <div className="toolbar">
        <div className="search">
          <input
            ref={inputRef}
            type="search"
            value={text}
            placeholder={placeholder}
            aria-label={`Search ${title.toLowerCase()}`}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Escape') setText(''); }}
            autoFocus
          />
          <span className="kbd" aria-hidden="true">/</span>
        </div>
        <button type="button" className="btn" onClick={() => revalidator.revalidate()}>Reload</button>
      </div>
      {checkboxes && (
        <div className="filters">
          {checkboxes.map((box) => (
            <label key={box.key} className="check">
              <input
                type="checkbox"
                checked={Boolean(filters[box.key])}
                onChange={(event) => update({ [box.key]: event.target.checked ? '1' : null, page: null })}
              />
              {box.label}
            </label>
          ))}
        </div>
      )}
      {hint && <p className="hint">{hint}</p>}

      <ErrorAlert error={error} />
      {data.note && <div className="alert note">{data.note}</div>}

      <div className="card">
        <div className={`table-scroll${loading ? ' is-loading' : ''}`}>
          <table className="data">
            <thead>
              <tr>
                {columns.map((col) => {
                  const sorted = col.sortField && col.sortField === sortField;
                  return (
                    <th
                      key={col.key}
                      className={cellClass(col, col.numeric ? 'num' : undefined)}
                      aria-sort={sorted ? (desc ? 'descending' : 'ascending') : undefined}
                    >
                      {col.sortField ? (
                        <button type="button" className="sort" onClick={() => sortBy(col.sortField)}>
                          {col.label}
                          <span className="arrow" aria-hidden="true">{sorted && !desc ? '▲' : '▼'}</span>
                        </button>
                      ) : col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item._id}>
                  {columns.map((col) => (
                    <td key={col.key} className={cellClass(col, col.numeric ? 'num' : col.key === 'name' ? 'name' : undefined)}>
                      {col.render ? col.render(item) : item[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && !error && data.items.length === 0 && <div className="empty">No match.</div>}
        </div>
        <div className="pager">
          <div className="group">
            <span>Rows</span>
            <select className="select" aria-label="Rows per page" value={size} onChange={(e) => update({ size: e.target.value === '25' ? null : e.target.value, page: null })}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="group">
            <button type="button" className="btn" disabled={page <= 1} onClick={() => update({ page: page > 2 ? page - 1 : null })}>
              ← Previous
            </button>
            <span>Page {page}</span>
            <button type="button" className="btn" disabled={!data.hasNext} onClick={() => update({ page: page + 1 })}>
              Next →
            </button>
          </div>
        </div>
      </div>
      {data.sql && (
        <details className="sql">
          <summary>Query</summary>
          <code>{data.sql}</code>
        </details>
      )}
    </>
  );
}
